import { VStack } from "@chakra-ui/react";
import { useHistory } from "react-router-dom";

import { useEditUserAppointment } from "modules/user/infrastructure/useEditUserAppointment";
import { IAppointmentDetails } from "modules/user/application/types/IAppointmentDetails";
import { IEReservationIntervalElement } from "modules/appointment/types/IEReservationIntervalElement";
import { IUserEditAppointmentForm } from "modules/user/application/types/IUserEditAppointmentForm";
import { IEReservationQueueFormField } from "modules/queues/types/IEReservationQueueFormField";
import { QueueField } from "modules/appointment/presentation/QueueField";
import { useEditUserAppointmentNotification } from "modules/appointment/presentation/useEditUserAppointmentNotification";

import { useGetFriendlyUrl } from "utils/hooks/useGetFriendlyUrl";

import { Form, IErrorField, SpinnerButton, TextField } from "components/Form";
import { ChakraText } from "components/ChakraText";

import { useEditUserAppointmentFormValidationSchema } from "./useEditUserAppointmentFormValidationSchema";

interface IProps {
  isOpen: boolean;
  appointment: IAppointmentDetails;
  intervalElement: IEReservationIntervalElement;
  formFields: IEReservationQueueFormField[];
}

const EditUserAppointmentForm = ({
  appointment,
  intervalElement,
  formFields,
}: IProps) => {
  const {
    showErrorNotification,
    showSuccessNotification,
  } = useEditUserAppointmentNotification();

  const { friendlyUrl } = useGetFriendlyUrl();
  const history = useHistory();
  const editUserAppointmentFormValidationSchema = useEditUserAppointmentFormValidationSchema(
    formFields
  );

  const mutation = useEditUserAppointment(
    intervalElement.id,
    appointment.appointmentId
  );

  const handleSubmit = async (
    model: IUserEditAppointmentForm,
    setCustomErrors: (errors: IErrorField[]) => void
  ) => {
    const formData = new FormData();
    Object.keys(model).forEach((key) => {
      formData.append(
        key,
        model[key as keyof typeof model]
          ? model[key as keyof typeof model].toString()
          : ""
      );
    });
    try {
      const res = await mutation.mutateAsync(formData);
      showSuccessNotification();
      history.push(
        `/e-rezerwacja/${friendlyUrl}/uzytkownik/aktualne-wizyty/${res.id}`
      );
    } catch (e) {
      if (e.status === 400) {
        setCustomErrors(e.response.msg);
        showErrorNotification(e.message);
        return;
      }
      showErrorNotification();
    }
  };

  return (
    <Form<IUserEditAppointmentForm>
      onSubmit={handleSubmit}
      defaultValues={{
        email: appointment.email,
        name: appointment.userName,
        phone: appointment.userPhone,
        details: appointment.details,
      }}
      validationSchema={editUserAppointmentFormValidationSchema}
      resetOnSubmit={false}
    >
      {() => {
        return (
          <VStack spacing={4} align="stretch">
            <TextField label="Adres email" name="email" isDisabled />
            {formFields.map((field) => {
              return <QueueField field={field} />;
            })}
            <VStack spacing={4} mt={8}>
              <SpinnerButton
                id="confirm-edit-user-appointment-btn"
                btnTitle={<ChakraText>Potwierdź zmianę terminu</ChakraText>}
                w="250px"
              />
            </VStack>
          </VStack>
        );
      }}
    </Form>
  );
};

export { EditUserAppointmentForm };
